/**
 * ============================================================================
 * USER SIDEBAR COMPONENT
 * ============================================================================
 * Sidebar navigation for user pages – green/black theme, sleek styling
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../providers';
import { useI18n } from '../../providers';
import { useWalletStore } from '../../store';
import '../../styles/landing.css';

const UserSidebar = ({ isOpen = true, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthContext();
  const { language } = useI18n();
  const { balance } = useWalletStore();

  const menuItems = [
    { id: 'dashboard', label: language === 'fr' ? 'Tableau de bord' : 'Dashboard', icon: '📊', path: '/dashboard' },
    { id: 'transactions', label: language === 'fr' ? 'Transactions' : 'Transactions', icon: '💳', path: '/transactions' },
    { id: 'wallet', label: language === 'fr' ? 'Portefeuille' : 'Wallet', icon: '💰', path: '/wallet' },
    { id: 'analytics', label: language === 'fr' ? 'Analytique' : 'Analytics', icon: '📈', path: '/analytics' },
    { id: 'profile', label: language === 'fr' ? 'Profil' : 'Profile', icon: '👤', path: '/profile' },
    { id: 'settings', label: language === 'fr' ? 'Paramètres' : 'Settings', icon: '⚙️', path: '/settings' },
  ];

  const currentPath = location.pathname;
  const activeItem = menuItems.find((item) =>
    currentPath === '/' ? item.id === 'dashboard' : currentPath.startsWith(item.path)
  )?.id || 'dashboard';

  const handleNavigate = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  const verificationLevel = user?.verificationLevel || 'unverified';
  const isVerified = verificationLevel === 'verified';

  return (
    <>
      {isOpen && (
        <div
          className="user-sidebar-overlay"
          onClick={onClose}
          aria-hidden
        />
      )}

      <aside
        className={`user-sidebar ${isOpen ? 'user-sidebar-open' : ''}`}
        aria-label={language === 'fr' ? 'Navigation principale' : 'Main navigation'}
      >
        <div className="user-sidebar-inner">
          {/* Header – close only (no WekaTrust / 3 Taps) */}
          <div className="user-sidebar-header">
            <span className="flex-1 min-w-0" aria-hidden />
            <button
              type="button"
              onClick={onClose}
              className="user-sidebar-close"
              aria-label={language === 'fr' ? 'Fermer le menu' : 'Close menu'}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Wallet strip */}
          <div className="user-sidebar-wallet">
            <div className="user-sidebar-wallet-top">
              <span className="user-sidebar-wallet-label">{language === 'fr' ? 'Solde' : 'Balance'}</span>
              <span className={`user-sidebar-wallet-badge ${isVerified ? 'user-sidebar-wallet-badge-verified' : ''}`}>
                {isVerified ? (language === 'fr' ? 'Vérifié' : 'Verified') : (language === 'fr' ? 'Non vérifié' : 'Unverified')}
              </span>
            </div>
            <div className="user-sidebar-wallet-amount">
              {balance?.available != null ? `${Number(balance.available).toLocaleString()} XAF` : '0 XAF'}
            </div>
            <button type="button" onClick={() => handleNavigate('/wallet')} className="user-sidebar-wallet-btn">
              {language === 'fr' ? 'Voir le portefeuille' : 'View Wallet'}
            </button>
          </div>

          {/* Nav */}
          <nav className="user-sidebar-nav">
            {menuItems.map((item) => {
              const isActive = activeItem === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleNavigate(item.path)}
                  className={`user-sidebar-item ${isActive ? 'user-sidebar-item-active' : ''}`}
                >
                  <span className="user-sidebar-item-icon">{item.icon}</span>
                  <span className="user-sidebar-item-label">{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Quick actions */}
          <div className="user-sidebar-actions">
            <button
              type="button"
              onClick={() => handleNavigate('/transactions/create')}
              className="user-sidebar-cta"
            >
              <span className="user-sidebar-cta-icon">+</span>
              {language === 'fr' ? 'Nouvelle transaction' : 'New Transaction'}
            </button>
            <button
              type="button"
              onClick={() => handleNavigate('/verification')}
              className="user-sidebar-secondary"
            >
              {language === 'fr' ? 'Vérifier mon compte' : 'Verify Account'}
            </button>
          </div>

          {/* Footer */}
          <div className="user-sidebar-footer">
            <div className="user-sidebar-secure">
              <span className="user-sidebar-secure-dot" aria-hidden />
              <span className="user-sidebar-secure-text">{language === 'fr' ? 'Sécurisé' : 'Secure'}</span>
            </div>
            <p className="user-sidebar-secure-desc">
              {language === 'fr' ? 'Votre argent est protégé' : 'Your money is protected'}
            </p>
          </div>
        </div>
      </aside>
    </>
  );
};

export default UserSidebar;
